import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconTrash, IconX } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { Button, Dropdown, Form, Modal, Nav } from "react-bootstrap";
import Select, { components } from "react-select";
import { usePostRequest } from "../../Utils/hooks/QueryHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PostRequest, UpdateRF } from "../../Utils/hooks/ApiHandler";
import { toast } from "sonner";

const filterSchema = z.object({
  filterName: z.string().min(1, "Filter Name is required"),
  filterDescription: z.string().optional(),
});

export const CampaigningFilter = ({
  setFilterQueryValues,
  filterQueryValues,
  dropdownOptions,
  filterState,
  setFilterState,
  filterDropdown,
  setFilterDropdown,
  setHeading,
  subFilter,
  customSubFilter,
  setTempFilterData,
  refetchTabData,
  setRefetchTabData,
  currentTab,
}) => {
  const filterRefs = useRef({});
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer");
  const headers = {
    Authorization: customer?.token?.authorization,
    Authentication: customer?.token?.authentication,
  };

  const [saveAsModal, setShowSaveAsModal] = useState(false);

  // FIXED: Dynamic card name based on current tab
  const getDefaultCardName = () => {
    switch (currentTab) {
      case "leads":
        return "All Leads - Copy";
      case "company":
        return "All Companies - Copy";
      case "contact":
        return "All Contacts - Copy";
      default:
        return "All Items - Copy";
    }
  };

  const [cardName, setCardName] = useState(getDefaultCardName());

  const saveOptions = [
    { value: "save", label: "Save" },
    { value: "save_as", label: "Save As..." },
  ];

  // empty value deetection state to check before user do save as
  const [filterValueErrors, setFilterValueErrors] = useState({}); // New state for input value errors

  // Initialize react-hook-form for the modal
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(filterSchema),
  });


  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <i
          class="fa-solid fa-plus"
          style={{
            color: "white",
            backgroundColor: "#007bff",
            padding: "5px",
            borderRadius: "4px",
            cursor: "pointer",
            border: 0,
            outline: 0,
          }}
        ></i>
      </components.DropdownIndicator>
    );
  };

  const handleAddFilter = (option) => {
    const existingFilter = filterState.find((f) => f.field === option.value);
    if (existingFilter) {
      // Scroll to existing filter if it's already added
      filterRefs.current[existingFilter.id]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      // Optionally, add a temporary highlight class here if needed
      return;
    }

    let mappedOption = null;
    if (["owner_id", "visible_to"].includes(option.value)) {
      mappedOption = option.option?.map((opt) => ({
        value: opt.leadId,
        label: opt.leadName,
      }));
    } else if (option.value === "lead_source_id") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.lead_source_id,
        label: opt.source_title,
      }));
    } else if (option.value === "company_id") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.company_id,
        label: opt.company_name,
      }));
    } else if (option.value === "country") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.countryId,
        label: opt.countryName,
      }));
    } else if (option.value === "state") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.stateCode,
        label: opt.stateName,
      }));
    } else if (option.value === "city") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.cityId,
        label: opt.cityName,
      }));
    } else if (option.value === "industry") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    } else if (option.value === "business_type") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    } else if (option.value === "employee_size") {
      mappedOption = option.option?.map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    }

    // Add new filter
    const newFilter = {
      id: option.value, // Generate a unique ID for the filter row
      field: option.value,
      label_name: option.label,
      option: option.option ? mappedOption : null,
      subFilterType: "equals", // Default sub-filter value
      value: "",
    };

    setFilterState((prev) => [...prev, newFilter]);
    // Clear any existing value errors when a new filter is added
    setFilterValueErrors({});
  };

  // to remove a particular field from the array
  const handleRemoveFilter = (id) => {
    setFilterState((prev) => prev.filter((filter) => filter.id !== id));
    // setTempFilterData(null)
    // Remove error for the deleted filter
    setFilterValueErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  // to change the subfilter value with respect to the particular field determined by its id
  const handleSubFilterChange = (id, selectedOption) => {
    setFilterState((prev) =>
      prev.map((filter) =>
        filter.id === id
          ? { ...filter, subFilterType: selectedOption.value }
          : filter
      )
    );
  };

  // adding new fields to the array.
  const handleValueChange = (id, newValue) => {
    setFilterState((prev) =>
      prev.map((filter) =>
        filter.id === id ? { ...filter, value: newValue } : filter
      )
    );
    // Clear error for this specific input when its value changes
    setFilterValueErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  // New function to validate all filter input values
  const validateAllFilterValues = () => {
    const newErrors = {};
    let isValid = true;

    filterState.forEach((filter) => {
      // Only validate if subFilterType is not 'is_empty' or 'is_not_empty'
      if (
        !["is_empty", "is_not_empty"].includes(filter.subFilterType) &&
        filter.value.trim() === ""
      ) {
        newErrors[filter.id] = "This field is required";
        isValid = false;
      }
    });

    setFilterValueErrors(newErrors);
    return isValid;
  };

  // FIXED: Dynamic filter title based on current tab
  const getFilterTitle = () => {
    switch (currentTab) {
      case "leads":
        return "Filter By Leads";
      case "company":
        return "Filter By Companies";
      case "contact":
        return "Filter By Contacts";
      default:
        return "Filter";
    }
  };

  // apply api would be called within this
  const handleApply = async () => {
    if (filterState?.length === 0) {
      setTempFilterData(null);
      return;
    }

    if (!validateAllFilterValues()) {
      return;
    }

    const applyData = [];
    filterState.map((f) => {
      const data = {
        label_name: f.field,
        label_query: f.subFilterType,
        label_value: f.value,
      };
      applyData.push(data);
    });

    setFilterQueryValues(applyData);

    const applyPayload = {
      customer_id: customer.customer_details?.customer_id,
      filter_values: applyData,
    };

    // FIXED: Added proper endpoint handling for all three tabs
    let applyEndpoint = "";
    if (currentTab === "contact") {
      applyEndpoint = "contacts/applyFilter";
    } else if (currentTab === "company") {
      applyEndpoint = "company/applyFilter";
    } else if (currentTab === "leads") {
      applyEndpoint = "lead/applyFilter"; // FIXED: Added missing leads endpoint
    }

    // FIXED: Added validation for endpoint
    if (!applyEndpoint) {
      toast.error("Invalid tab selection for filtering");
      return;
    }

    try {
      const res = await PostRequest(
        applyEndpoint,
        applyPayload,
        false,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate }
      );

      if (res.success === false) {
        toast.error(res.message);
        return;
      }

      setTempFilterData(res && res?.data);
      setFilterDropdown(false);
    } catch (error) {
      console.error("Filter apply error:", error);
      toast.error("Error applying filter");
    }
  };


  // FIXED: Added proper endpoint handling for save functionality
  let endpoint = "";
  if (currentTab === "contact") {
    endpoint = "contacts/filterQuery";
  } else if (currentTab === "company") {
    endpoint = "company/filterQuery";
  } else if (currentTab === "leads") {
    endpoint = "lead/filterQuery"; // FIXED: Added missing leads endpoint
  }

  const { mutate } = usePostRequest(
    endpoint,
    false,
    { headers },
    { customer, setCustomer, rf: headers.Authentication, navigate }
  );

  const onSubmit = (formData) => {
    const filterData = [];
    filterState.map((f) => {
      const data = {
        label_name: f.field,
        label_query: f.subFilterType,
        label_value: f.value,
      };
      filterData.push(data);
    });

    const finalData = {
      customer_id: customer.customer_details?.customer_id,
      card_name:
        formData.filterName === cardName ? cardName : formData.filterName,
      description: formData.filterDescription,
      filter_values: filterData,
      user_login_id: customer.customer_details?.id,
    };

    // FIXED: Added validation for endpoint before mutation
    if (!endpoint) {
      toast.error("Invalid tab selection for saving filter");
      return;
    }

    // within mutate if response is success then
    mutate(finalData, {
      onSuccess: (data) => {
        if (data.status === "fail") {
          toast.error(data.message);
          return;
        }
        if (data.status === "error") {
          return;
        }
        reset();
        setRefetchTabData(!refetchTabData);
        setHeading &&
          setHeading(
            formData.filterName === cardName ? cardName : formData.filterName
          );
        localStorage.setItem(
          "last_heading",
          formData.filterName === cardName ? cardName : formData.filterName
        );
        // also you need to make sure that the top heading gets changed and the data received here would be passed up to the parent component where it would pass the data to the editcontact.
        setShowSaveAsModal(false);
        setFilterDropdown(false);
        setFilterDropdown(false)
                        setTempFilterData(null);
                        setFilterState([]); // Clear filter states
                        setFilterQueryValues([]); // Clear filter query values
                        
        toast.success(data.message);
      },
      onError: (error) => {
        console.error("Save filter error:", error);
        toast.error("Error saving filter");
      },
    });
  };

  return (
    <Dropdown
      show={filterDropdown}
      as="div"
      autoClose={false}
      className="dropdownsWithoutToggle"
    >
      <Dropdown.Toggle
        onClick={() => setFilterDropdown(!filterDropdown)}
        as={Nav.Link}
        className="header-link"
      >
        <button
          className="py-2 w-[30px] d-flex gap-1 rounded-1 btn-default-outline justify-content-center"
          style={{
            fontSize: "1rem",
            alignItems: "center",
            height: "35px",
            width: "2.5rem",
          }}
        >
          {" "}
          <FontAwesomeIcon
            style={{
              width: "15px",
              height: "15px",
            }}
            icon={faFilter}
          />
        </button>
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="defaultDropdownLong main-header-dropdown header-profile-dropdown plusDrowpdown dropdown-menu-end"
        style={{ right: "3.4rem" }}
      >
        <div className="d-flex align-items-center justify-content-between mx-2">
          {/* FIXED: Dynamic filter title based on current tab */}
          <div className="mb-2 mt-2 fw-bold">{getFilterTitle()}</div>
          <button
            className="btn btn-sm btn-outline-secondary p-1"
            onClick={() => setFilterDropdown(false)}
            style={{
              width: "24px",
              height: "24px",
              lineHeight: "1",
              border: "0px",
            }}
          >
            <IconX stroke={2} style={{ width: "18px", height: "18px" }} />
          </button>
        </div>
        {/* Input with plus icon on the end */}
        <div className="mx-2">
          <Select
            className="border-0 bg-transparent shadow-none"
            placeholder="Add a Filter"
            classNamePrefix="react-select"
            isSearchable
            onChange={handleAddFilter}
            options={dropdownOptions}
            components={{ DropdownIndicator }}
            value={null}
          />
        </div>
        <hr style={{ marginBottom: "0px" }} />
        {/* section where filter would be shown */}
        {filterState?.length === 0 && (
          <p className="text-center text-gray-500 py-4">
            No filters added yet.
          </p>
        )}
        <div
          style={{
            height: "320px",
            overflowY: "auto",
            scrollBehavior: "smooth",
          }}
        >
          {filterState?.map((filter) => (
            <div
              key={filter.id}
              ref={(el) => (filterRefs.current[filter.id] = el)}
              className="px-3"
            >
              <div className="d-flex flex-column mt-3 gap-1 ">
                <div className="d-flex justify-content-between align-items-center ">
                  <h6 style={{ fontSize: "14px" }}>{filter.label_name}</h6>
                  <button
                    aria-label={`Remove filter`}
                    onClick={() => handleRemoveFilter(filter.id)}
                    style={{
                      border: 0,
                      borderRadius: "5px",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "5px",
                    }}
                  >
                    <IconTrash
                      stroke={2}
                      style={{ width: "12px", height: "12px" }}
                    />
                  </button>
                </div>
                <Select
                  className="border-0 bg-transparent shadow-none"
                  classNamePrefix="react-select"
                  isSearchable
                  onChange={(option) =>
                    handleSubFilterChange(filter.id, option)
                  }
                  options={
                    filter.id === "visible_to" ||
                    filter.id === "company_id" ||
                    filter.id === "owner_id" ||
                    filter.id === "lead_source_id"
                      ? customSubFilter
                      : subFilter
                  }
                  value={subFilter.find(
                    (option) => option.value === filter.subFilterType
                  )}
                  menuPortalTarget={document.body} // Render menu to body
                  menuPosition="fixed" // Position fixed relative to viewport
                  styles={{
                    singleValue: (base) => ({
                      ...base,
                      fontSize: "14px !important",
                      lineHeight: "1.2",
                      color: "#333", // optional: confirm it's not transparent
                    }),
                    menu: (base) => ({
                      ...base,
                      padding: 0,
                      marginTop: 0, // optional: reduce distance from select box
                    }),
                    menuList: (base) => ({
                      ...base,
                      maxHeight: "120px", // 🔽 limit visible dropdown height
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                    option: (base) => ({
                      ...base,
                      padding: "4px 8px",
                      fontSize: "14px",
                    }),
                  }}
                />
                {filter.option === null ? (
                  !["is_empty", "is_not_empty"].includes(
                    filter.subFilterType
                  ) && (
                    <div className="">
                      <input
                        value={filter.value}
                        onChange={(e) =>
                          handleValueChange(filter.id, e.target.value)
                        }
                        type="text"
                        className={`form-control ${
                          filterValueErrors[filter.id] ? "is-invalid" : ""
                        }`}
                        placeholder="Enter Value"
                      />
                      {filterValueErrors[filter.id] && (
                        <div
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        >
                          {filterValueErrors[filter.id]}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div className="">
                    <Select
                      className="border-0 bg-transparent shadow-none"
                      classNamePrefix="react-select"
                      isSearchable
                      onChange={(selectedOption) =>
                        handleValueChange(
                          filter.id,
                          selectedOption ? selectedOption.value : ""
                        )
                      }
                      options={filter.option}
                      value={filter.option?.find(
                        (opt) => opt.value === filter.value
                      )}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {filterValueErrors[filter.id] && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        {filterValueErrors[filter.id]}
                      </div>
                    )}
                  </div>
                )}
                <hr style={{ marginBottom: "0px" }} />
              </div>
            </div>
          ))}
        </div>
        <hr style={{ marginBottom: "0px", margin: "0px" }} />
        {/* footer section for the dropdown */}
        <div
          style={{ padding: "10px", fontSize: "14px" }}
          className="d-flex justify-content-between align-items-center"
        >
          <button
            onClick={() => {
              setFilterState([]);
              setTempFilterData(null);
              setFilterQueryValues([]); // FIXED: Also clear filter query values
            }}
            style={{ border: 0, background: "transparent" }}
          >
            Clear
          </button>
          <div className="d-flex gap-2 ">
            {filterState?.length > 0 ? (
              // select dropdown for save and save as
              <Select
                className="w-32"
                classNamePrefix="react-select-save"
                isSearchable={false}
                isClearable={false}
                options={saveOptions}
                onChange={(selectedOption) => {
                  if (selectedOption.value === "save") {
                    if (!validateAllFilterValues()) {
                      return; // Stop if validation fails
                    }
                    // make sure here you add logic for the persistence of the filter values for the currently selected filter. the filter could be a custom one or the default all contacts
                  } else if (selectedOption.value === "save_as") {
                    if (!validateAllFilterValues()) {
                      return; // Stop if validation fails
                    }
                    setCardName(getDefaultCardName()); // FIXED: Update card name based on current tab
                    setShowSaveAsModal(true);
                  }
                }}
                value={saveOptions[0]}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: "4px",
                    border: "1px solid #007bff !important",
                    backgroundColor: "#007bff !important",
                    color: "white !important",
                    boxShadow: "none !important",
                    minHeight: "38px",
                    "&:hover": {
                      backgroundColor: "#0056b3 !important",
                    },
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: "white !important",
                    fontSize: "14px",
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    backgroundColor: "rgba(255,255,255,0.5) !important",
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: "white !important",
                    "&:hover": {
                      color: "white !important",
                    },
                  }),
                  menu: (base) => ({
                    ...base,
                    marginTop: "4px",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }),
                  option: (base, state) => ({
                    ...base,
                    fontSize: "14px",
                    padding: "8px 12px",
                    backgroundColor: state.isFocused ? "#e2e8f0" : "white",
                    color: "#333",
                    "&:active": {
                      backgroundColor: "#cbd5e1",
                    },
                  }),
                }}
              />
            ) : (
              // Show disabled "Save As" button if no filters are present
              <button disabled className="px-4 py-2 saveAsBtn">
                Save As
              </button>
            )}
            <button
              onClick={handleApply}
              style={{
                borderRadius: "4px",
                border: "1px solid #007bff",
                backgroundColor: "#007bff",
                color: "white",
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </Dropdown.Menu>
      {saveAsModal && filterState?.length > 0 && (
        <Modal
          show={setShowSaveAsModal}
          onHide={() => setShowSaveAsModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Save Filter</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Form.Group className="mb-3" controlId="filterName">
                <Form.Label className="w-auto">
                  Filter Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  defaultValue={cardName}
                  placeholder="Enter filter name"
                  {...register("filterName")}
                  isInvalid={!!errors.filterName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.filterName?.message}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group className="mb-3" controlId="filterDescription">
                <Form.Label className="w-auto">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  className="placeholder-sm"
                  placeholder="Enter filter description"
                  {...register("filterDescription")}
                />
              </Form.Group>
              <Modal.Footer>
                <Button
                  className="rounded-2"
                  variant="custom-variant"
                  style={{ border: "1px solid black", width: "90px" }}
                  onClick={() => {
                    // make sure you reset the form here
                    reset();
                    setShowSaveAsModal(false);
                  }}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  className="rounded-2"
                  variant="action-btn"
                  style={{ width: "130px" }}
                >
                  Save Changes
                </Button>
              </Modal.Footer>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </Dropdown>
  );
};
